import React from 'react'
import { Upload, QualityEnum } from "../interface/Upload"
import UploadSwitcher from "./media/UploadSwitcher"
import * as UploadUtil from "../library/UploadUtil"

interface UploadControllerProps {
  show: boolean
  uploads: Upload[]
  cycleCount: number
  expectedGroupSize: number
  quality: QualityEnum
  transitionName: string
  photoDelay: number // milliseconds
  cycleStartDelay: number // milliseconds
  cycleEndDelay: number // milliseconds
  didFinishCycle: Function
  deletedCount: number
  debug: boolean
}

interface UploadControllerState {
  displaying: Upload[]
  animationCount: number
  animationKeys: number[],
  currentIndex: number
}

export default class UploadController extends React.Component<UploadControllerProps, UploadControllerState> {
  finishTimer?: any
  animateTimer?: any

  constructor(props: UploadControllerProps) {
    super(props)

    this.state = {
      currentIndex: 0,
      displaying: UploadUtil.getPlaceholderUploads(props.expectedGroupSize),
      animationCount: 0,
      animationKeys: this.getPlaceholderAnimationKeys(props.expectedGroupSize)
    }
  }

  getPlaceholderAnimationKeys(length: number) {
    let animationKeys = []
    for (let i = 0; i < length; i++) {
      animationKeys.push(0)
    }
    return animationKeys
  }

  componentDidMount() {
    this.animate()
  }

  componentWillUnmount() {
    clearTimeout(this.finishTimer)
    clearTimeout(this.animateTimer)
  }

  componentDidUpdate(prevProps: UploadControllerProps, _prevState: UploadControllerState) {
    // Don't try to restart or animate if it's not supposed to be showing
    if (!this.props.show) {
      return
    }

    if (!prevProps.show
      || JSON.stringify(this.props.uploads) !== JSON.stringify(prevProps.uploads)
      || this.props.cycleCount !== prevProps.cycleCount
    ) {
      this.animate(0, this.props.cycleStartDelay)
    }

    if (this.props.expectedGroupSize !== prevProps.expectedGroupSize
      || this.props.transitionName !== prevProps.transitionName
      || this.props.deletedCount !== prevProps.deletedCount
    ) {
      this.restart()
    }
  }

  restart() {
    this.props.debug && console.log(`Restart upload controller`)

    clearTimeout(this.animateTimer)
    clearTimeout(this.finishTimer)

    // It's simpler to flush the upload switchers completely and add new ones than to try to update their state
    this.setState({
      displaying: []
    },
      () => {
        this.setState({
          displaying: UploadUtil.getPlaceholderUploads(this.props.expectedGroupSize)
        }, this.animate)
      }
    )
  }

  animate(index: number = 0, startDelay: number = 0) {

    const increment = () => {
      let upload = this.props.uploads[index]
      let displaying = [...this.state.displaying]
      let animationKeys = [...this.state.animationKeys]
      let animationCount = this.state.animationCount + 1

      if (index <= displaying.length - 1) {
        displaying[index] = upload
        animationKeys[index] = animationCount
      } else {
        displaying.push(upload)
        animationKeys.push(animationCount)
      }

      this.setState({
        displaying: displaying,
        animationKeys: animationKeys,
        animationCount: animationCount,
        currentIndex: index
      })
    }

    this.props.debug && console.log(`Animate upload at index ${index} with delay ${startDelay}`)

    clearTimeout(this.animateTimer)
    this.animateTimer = setTimeout(increment, startDelay)
  }

  handleUploadLoaded(_uploadId: string) {
    if (this.state.currentIndex === this.props.uploads.length - 1) {
      clearTimeout(this.finishTimer)
      this.finishTimer = setTimeout(this.props.didFinishCycle, this.props.cycleEndDelay + this.props.photoDelay)
      this.props.debug && console.log(`Will finish upload cycle in ${this.props.cycleEndDelay}ms`)
    } else {
      this.animate(this.state.currentIndex + 1, this.props.photoDelay)
    }
  }

  render() {
    return (
      <>
        {this.state.displaying.map((upload, index) => (
          <UploadSwitcher
            key={index}
            upload={upload}
            animationKey={this.state.animationKeys[index]}
            transition={this.props.transitionName}
            isActive={this.state.currentIndex === index}
            quality={this.props.quality}
            didLoad={(uploadId) => this.handleUploadLoaded(uploadId)}
          />
        ))}
      </>
    )
  }
}